import {
  addArticleToVolumeTypes,
  CreateVolumePayload,
  publishVolumeTypes,
  removeArticleFromVolumeTypes,
  requestVolumeTypes,
  setArticleOrderOnVolumeTypes,
  VolumeProduction,
  volumesTypes,
} from "./volumesTypes";
import {
  createApiClient,
  handleGetRequest,
  handlePostRequest,
} from "../apiService";
import { useToken } from "../tokenUtils";

import { getSubdomain } from "../../utils/getSubdomain/subdomain";

const apiClient = createApiClient("/");
const domain = getSubdomain();

const handlePatchRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.patch(url, data, { headers });
  return res.data;
};

const getVolumes = async (): Promise<volumesTypes[]> => {
  const volumes = await apiClient.get<volumesTypes[]>(`${domain}/volumes/`);
  return volumes.data;
};

const getAllVolumes = async (): Promise<volumesTypes[]> => {
  const token = useToken();

  const volumes = await apiClient.get<volumesTypes[]>(
    `${domain}/all_volumes/`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return volumes.data;
};

const getVolume = async (slug: string): Promise<volumesTypes> => {
  const volumes = await apiClient.get<volumesTypes>(
    `${domain}/volumes/${slug}/`,
  );
  return volumes.data;
};

const getVolumesProduction = async (): Promise<VolumeProduction[]> => {
  const volumes = await apiClient.get<VolumeProduction[]>(
    `${domain}/volumes/production`,
  );
  return volumes.data;
};
const createVolume = async (data: CreateVolumePayload) => {
  const token = useToken();

  const res = await apiClient.post(`${domain}/volumes/`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return res.data;
};

const editVolume = async (data: requestVolumeTypes): Promise<volumesTypes> => {
  const token = useToken();

  const res = await handlePatchRequest(
    `${domain}/volumes/${data.slug}/`,
    data,
    token,
  );
  return res;
};

const registerVolumeDownload = async (volume_slug: string) => {
  const token = useToken();
  const res = await handlePostRequest(
    apiClient,
    `${domain}/volumes/${volume_slug}/download/`,
    {},
    token,
  );
  return res;
};

const registerVolumeDownloadNoLogin = async (volume_slug) => {
  const res = await handlePostRequest(
    apiClient,
    `${domain}/volumes/${volume_slug}/download-no-login/`,
    {},
    null,
  );
  return res;
};

const getVolumeBySlug = async (volume_slug: string): Promise<volumesTypes> => {
  const res = await handleGetRequest<volumesTypes>(
    apiClient,
    `${domain}/volumes/${volume_slug}/`,
  );
  return res;
};

const deleteVolume = async (slug: string) => {
  const token = useToken();
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  const response = await apiClient.delete(`${domain}/volumes/${slug}/`, {
    headers,
  });
  if (!response) {
    throw new Error("No data received from server");
  }
  return response.data;
};

const deleteFileFromVolume = async (slug: string) => {
  const token = useToken();
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };

  const response = await apiClient.delete(`${domain}/volumes/${slug}/files`, {
    headers,
  });

  if (!response) {
    throw new Error("No data received from server");
  }

  return response.data;
};

const addArticleToVolume = async (data: addArticleToVolumeTypes) => {
  const token = useToken();

  const response = await apiClient.put(
    `${domain}/volumes/${data.volume_slug}/add/${data.article_slug}/`,
    JSON.stringify(data),
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response) {
    throw new Error(`HTTP error! status: ${response}`);
  }

  return await response.data;
};

const removeArticleFromVolume = async (data: removeArticleFromVolumeTypes) => {
  const token = useToken();

  const response = await apiClient.delete(
    `${domain}/volumes/${data.volume_slug}/remove/${data.article_slug}/`,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response) {
    throw new Error(`HTTP error! status: ${response}`);
  }

  return await response.data;
};

const addPDFVolume = async (formData: FormData) => {
  const token = useToken();

  const volume_slug = formData.get("volume_slug");

  const headers = {
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.put(
    `${domain}/volumes/${volume_slug}/add_file/main`,
    formData,
    { headers },
  );
  return res.data;
};

const publishVolume = async (data: publishVolumeTypes) => {
  const token = useToken();

  const response = await apiClient.put(
    `${domain}/volumes/${data.volume_slug}/publish/`,
    JSON.stringify(data),
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response) {
    throw new Error(`HTTP error! status: ${response}`);
  }

  return await response.data;
};
const setArticleOrderOnVolume = async (
  setArticleOrderOnVolumeRequest: setArticleOrderOnVolumeTypes,
) => {
  const token = useToken();
  const response = await apiClient.put(
    `${domain}/volumes/${setArticleOrderOnVolumeRequest.volume_slug}/ordering`,
    setArticleOrderOnVolumeRequest.articles,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

const getInitialVolume = async (): Promise<volumesTypes> => {
  try {
    const volumes = await apiClient.get<volumesTypes>(
      `${domain}/volume/initial`,
    );

    return volumes.data;
  } catch (error) {
    console.error("Error al obtener los volúmenes:", error);
    throw error;
  }
};
const volumesService = {
  createVolume,
  getVolumes,
  getAllVolumes,
  getVolumesProduction,
  getVolume,
  editVolume,
  registerVolumeDownload,
  getVolumeBySlug,
  deleteVolume,
  deleteFileFromVolume,
  addArticleToVolume,
  removeArticleFromVolume,
  addPDFVolume,
  publishVolume,
  setArticleOrderOnVolume,
  getInitialVolume,
  registerVolumeDownloadNoLogin,
};

export default volumesService;
